"use client";

import { Combobox } from "@/components/ui/combobox";
import type { City } from "@/lib/supabase/types";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";

interface CitySelectorProps {
	cities: City[] | null;
}

export default function CitySelector({ cities }: CitySelectorProps) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const currentCitiesParam = searchParams.get("cities") || "";

	// Handle multiple cities from URL (comma-separated)
	const currentCities = currentCitiesParam
		? currentCitiesParam.split(",").map((city) => city.trim())
		: [];

	const [query, setQuery] = useState("");
	const [selectedCities, setSelectedCities] = useState<string[]>(currentCities);

	const handleChange = (selected: string[]) => {
		setSelectedCities(selected);
		const params = new URLSearchParams(searchParams.toString());

		if (selected) {
			if (Array.isArray(selected)) {
				// Handle multiple selection
				if (selected.length > 0) {
					const cityValues = selected.map((s) => s);
					params.set("cities", cityValues.join(","));
				} else {
					params.delete("cities");
				}
			} else {
				// Handle single selection
				params.set("cities", selected);
			}
		} else {
			params.delete("cities");
		}

		// Preserve other search parameters
		const queryString = params.toString();
		const href = queryString ? `/blog?${queryString}` : "/blog";

		router.push(href);
	};

	const filteredCities =
		query === ""
			? cities
			: cities?.filter((city) => {
					return city.name.toLowerCase().includes(query.toLowerCase());
				});

	return (
		<Combobox
			options={
				filteredCities?.map((city) => ({
					value: city.name,
					label: city.name,
				})) || []
			}
			placeholder="City"
			selected={selectedCities}
			onChange={handleChange}
			className="h-7 text-xs px-1.5 gap-1"
		/>
	);
}
